import { isWasteContainerTypeCode, WasteContainerTypeCode } from "../constants/wasteContainerTypes";
import { dotIdNumberProblem } from "./dotIdNumber";
import { INHALATION_HAZARD_MARKING } from "./hazardousWasteLabelVisuals";

/**
 * Which set of DOT markings a container carries, by its container-type code.
 * The three groups partition the 13 codes in `wasteContainerTypes.ts` with no
 * remainder. Which marking each group takes: docs/waste/dot-label-rendering.md.
 */
export type DotMarkingGroup = "PACKAGE" | "BULK" | "PORTABLE_TANK";

/**
 * The group a code falls in when nothing is known about how much it holds.
 * Every code except TP can hold either side of the bulk threshold in
 * principle, so this is the historical reading, not the answer.
 */
const GROUP_BY_CODE: Record<WasteContainerTypeCode, DotMarkingGroup> = {
  BA: "PACKAGE",
  CF: "PACKAGE",
  CW: "PACKAGE",
  CY: "PACKAGE",
  DF: "PACKAGE",
  DM: "PACKAGE",
  DW: "PACKAGE",
  CM: "BULK",
  DT: "BULK",
  HG: "BULK",
  TC: "BULK",
  TT: "BULK",
  TP: "PORTABLE_TANK",
};

/**
 * Codes that name a vehicle or a rail car. Nothing they hold is small enough
 * to be a non-bulk package, so capacity does not come into it.
 */
const ALWAYS_BULK: WasteContainerTypeCode[] = ["DT", "HG", "TC", "TT"];

/** Gallons per unit, for the units that measure volume. */
const GALLONS_PER_UNIT: Record<string, number> = {
  GALLONS: 1,
  LITERS: 0.2641720524,
  BARRELS: 42,
  CUBIC_FEET: 7.480519481,
};

/** Pounds per unit, for the units that measure mass. */
const POUNDS_PER_UNIT: Record<string, number> = {
  POUNDS: 1,
  KILOGRAMS: 2.20462262,
  TONS: 2000,
  METRIC_TONS: 2204.62262,
};

/**
 * The 49 CFR 171.8 bulk thresholds. A container at or under its threshold is
 * a non-bulk package.
 *
 * The regulation states the solid case as net mass over 400kg AND capacity
 * over 450L. Only one of the two is usually recorded, so the measure that is
 * present decides. That is the reading the compliance reviewer gave.
 */
const BULK_OVER_GALLONS = 119;
const BULK_OVER_POUNDS_SOLID = 882;
const BULK_OVER_POUNDS_GAS_WATER_CAPACITY = 1000;

/** What the container holds in gallons, or undefined when it is not recorded
 * in a unit of volume. */
function gallonsOf(
  capacity: number | null | undefined,
  capacityUnit: string | null | undefined
): number | undefined {
  if (capacity === null || capacity === undefined || !capacityUnit) {
    return undefined;
  }
  const gallonsPer = GALLONS_PER_UNIT[capacityUnit.trim().toUpperCase()];
  return gallonsPer === undefined ? undefined : capacity * gallonsPer;
}

/**
 * Whether what the container holds puts it over the bulk threshold, or
 * undefined when the recorded capacity cannot answer.
 */
function exceedsBulkThreshold(
  capacity: number | null | undefined,
  capacityUnit: string | null | undefined,
  physicalState: string | null | undefined
): boolean | undefined {
  if (capacity === null || capacity === undefined || !capacityUnit) {
    return undefined;
  }
  const gallons = gallonsOf(capacity, capacityUnit);
  if (gallons !== undefined) {
    return gallons > BULK_OVER_GALLONS;
  }
  const unit = capacityUnit.trim().toUpperCase();
  const state = physicalState?.trim().toUpperCase();
  const poundsPer = POUNDS_PER_UNIT[unit];
  if (poundsPer === undefined) {
    return undefined;
  }
  const limit = state === "GAS" ? BULK_OVER_POUNDS_GAS_WATER_CAPACITY : BULK_OVER_POUNDS_SOLID;
  return capacity * poundsPer > limit;
}

/** What `dotMarkingGroupFor` reads. The enum fields are plain strings because
 * the API hands Oak PascalCase GraphQL enums and the lambdas SCREAMING_CASE
 * Prisma ones; both normalise to the same word here. */
export interface DotMarkingContainer {
  containerType: string;
  capacity?: number | null;
  capacityUnit?: string | null;
  physicalState?: string | null;
}

/**
 * Which DOT markings a container takes.
 *
 * Capacity decides it, not the type code: 49 CFR 171.8 defines bulk by how
 * much the packaging holds, and one code covers both sides of the line --
 * "CM" is "Box, carton, case, or roll-off (metal)", which is a metal crate at
 * one end and a roll-off dumpster at the other. A code that names a vehicle
 * or rail car is bulk whatever is recorded, and TP is a portable tank by
 * definition.
 *
 * Undefined for a container type that is not one of the 13 known codes.
 * `WasteContainer.containerType` is a plain String column, so legacy
 * free-text values reach here; they get no DOT marking rather than one
 * guessed from prose. A known code with no usable capacity falls back to what
 * that code has always meant.
 */
export function dotMarkingGroupFor(
  container: DotMarkingContainer | string
): DotMarkingGroup | undefined {
  const input = typeof container === "string" ? { containerType: container } : container;
  const code = input.containerType.trim().toUpperCase();
  if (!isWasteContainerTypeCode(code)) {
    return undefined;
  }
  if (code === "TP" || ALWAYS_BULK.includes(code)) {
    return GROUP_BY_CODE[code];
  }
  const exceeds = exceedsBulkThreshold(input.capacity, input.capacityUnit, input.physicalState);
  if (exceeds === undefined) {
    return GROUP_BY_CODE[code];
  }
  return exceeds ? "BULK" : "PACKAGE";
}

/** Bulk packagings under this volume are labeled instead of placarded
 * (172.400(a)(2), 172.514(c)(3)). */
const LABELED_BULK_UNDER_CUBIC_FEET = 640;
/** Portable tanks under this capacity are labeled instead of placarded
 * (172.400(a)(3), 172.514(c)(1)). */
const LABELED_PORTABLE_TANK_UNDER_GALLONS = 1000;

/**
 * Whether the regulated hazard-label pages (and the 4x6 diamond that points
 * at them) print for this container. A non-bulk package takes the label
 * (172.400(a)(1)). A bulk packaging under 640 cubic feet that is not a
 * vehicle or rail car, and a portable tank under 1,000 gallons, take labels
 * unless placarded (172.400(a)(2)-(3)), and 172.514(c) allows labels instead
 * of placards. A capacity we cannot read in volume prints the labels:
 * over-labeling is safe, under-labeling is not.
 */
export function requiresHazardLabel(
  group: DotMarkingGroup,
  container: DotMarkingContainer
): boolean {
  if (group === "PACKAGE") {
    return true;
  }
  const gallons = gallonsOf(container.capacity, container.capacityUnit);
  if (group === "PORTABLE_TANK") {
    return gallons === undefined || gallons < LABELED_PORTABLE_TANK_UNDER_GALLONS;
  }
  const code = container.containerType.trim().toUpperCase();
  if (ALWAYS_BULK.some((vehicleCode) => vehicleCode === code)) {
    return false;
  }
  return (
    gallons === undefined || gallons < LABELED_BULK_UNDER_CUBIC_FEET * GALLONS_PER_UNIT.CUBIC_FEET
  );
}

/** The 49 CFR 172.312 orientation arrows and the 172.301(a)(1) 12 mm ID
 * number are non-bulk rules. */
export function requiresNonBulkMarkings(group: DotMarkingGroup): boolean {
  return group === "PACKAGE";
}

/** Whether a package prints the 172.301(a)(1) marking: its shipping name and
 * its ID number at least 12 mm tall. A number without its UN, NA or ID prefix
 * is not that marking, so it prints none; the label dialog warns about it. */
export function printsIdMarkingStrip(
  container: DotMarkingContainer,
  idNumber: string | null | undefined
): boolean {
  const group = dotMarkingGroupFor(container);
  return (
    group !== undefined &&
    requiresNonBulkMarkings(group) &&
    Boolean(idNumber?.trim()) &&
    dotIdNumberProblem(idNumber) === undefined
  );
}

/** The 3.9in identification number on a contrasting background. */
export function requiresLargeIdNumber(group: DotMarkingGroup): boolean {
  return group === "BULK" || group === "PORTABLE_TANK";
}

/** The proper shipping name on two opposing sides -- portable tanks only. */
export function requiresShippingNameOnOpposingSides(group: DotMarkingGroup): boolean {
  return group === "PORTABLE_TANK";
}

/**
 * 49 CFR 172.302(a): the identification number goes on each side and each end
 * at 3,785 L (1,000 gallons) or more, and on two opposing sides below that.
 * A capacity we cannot read in gallons takes the larger marking -- marking
 * more than the section asks is permitted, marking less is not.
 */
const ID_NUMBER_EVERY_SIDE_OVER_GALLONS = 1000;

const RAIL_CAR_MARKING_HEIGHT_MM = 100;
const OTHER_BULK_MARKING_HEIGHT_MM = 50;
/** 172.302(b)(2) gives 12 mm for a portable tank under 3,785 L and 25 mm for
 * an IBC. Code TP covers both ("Portable tank / tote (IBC)"), so it takes the
 * larger: marking more than the section asks is permitted, less is not. */
const SMALL_PORTABLE_TANK_MARKING_HEIGHT_MM = 25;

/**
 * 49 CFR 172.302(b): the minimum height of a marking on a bulk packaging.
 * 100 mm on a rail car, 25 mm on a portable tank or IBC under 3,785 L (1,000
 * gallons), 50 mm on any other bulk packaging. A portable tank whose capacity
 * cannot be read in gallons takes the larger marking. The section covers bulk
 * packagings only, so PACKAGE returns the general 50 mm and callers do not ask.
 */
export function inhalationMarkingHeightMm(
  group: DotMarkingGroup,
  container?: DotMarkingContainer
): number {
  if (group === "PORTABLE_TANK") {
    const gallons = gallonsOf(container?.capacity, container?.capacityUnit);
    return gallons !== undefined && gallons < ID_NUMBER_EVERY_SIDE_OVER_GALLONS
      ? SMALL_PORTABLE_TANK_MARKING_HEIGHT_MM
      : OTHER_BULK_MARKING_HEIGHT_MM;
  }
  const isRailCar = group === "BULK" && container?.containerType.trim().toUpperCase() === "TC";
  return isRailCar ? RAIL_CAR_MARKING_HEIGHT_MM : OTHER_BULK_MARKING_HEIGHT_MM;
}

/** 49 CFR 172.313(a): a bulk packaging shows the inhalation marking on two
 * opposing sides, at the height 172.302(b) sets. */
export function inhalationMarkingInstruction(heightMm: number): string {
  return `Display ${INHALATION_HAZARD_MARKING} on two opposing sides, at least ${heightMm} mm tall.`;
}

/** 49 CFR 172.302(a): where the ID number goes, by capacity. */
export function idNumberInstruction(container?: DotMarkingContainer): string {
  const gallons = gallonsOf(container?.capacity, container?.capacityUnit);
  return gallons !== undefined && gallons < ID_NUMBER_EVERY_SIDE_OVER_GALLONS
    ? "Display this ID number on two opposing sides."
    : "Display this ID number on each side and each end.";
}

/** 49 CFR 172.326(a)-(b): a portable tank carries its proper shipping name on
 * two opposing sides, at the 172.302(b) height, and its owner's or lessee's
 * name. */
function portableTankInstruction(group: DotMarkingGroup, container?: DotMarkingContainer): string {
  const heightMm = inhalationMarkingHeightMm(group, container);
  return (
    `Display the shipping name on two opposing sides, at least ${heightMm} mm tall, ` +
    "and the owner's or lessee's name."
  );
}

/** 172.407(c)(1): a label is at least 100 mm a side, and fit-to-page on 4x6
 * stock shrinks the full-size pages to about 66 mm. */
const PRINT_AT_FULL_SCALE = "Print label pages at 100%.";

/** 49 CFR 172.406(e)(1), (e)(6): duplicate labels from 1.8 m3 (64 cubic feet). */
const DUPLICATE_LABELS_FROM_GALLONS = 64 * GALLONS_PER_UNIT.CUBIC_FEET;

/** 49 CFR 172.514(c): a bulk packaging that takes labels may be labeled
 * instead of placarded; the rest are placarded. 172.406(e) puts the labels on
 * at least two sides of a portable tank under 1,000 gallons, and of a
 * packaging of 64 cubic feet or more; an unrecorded volume gets two sides.
 * 172.406(a)(1)(ii) puts them near the shipping name marking, which a portable
 * tank carries, and 172.406(c) puts a primary and subsidiary next to each
 * other, within 150 mm. */
function labelOrPlacardInstruction(
  group: DotMarkingGroup,
  printsLabels: boolean,
  container?: DotMarkingContainer,
  printsBothLabels = false
): string {
  if (!printsLabels) {
    return "Placard per 49 CFR 172 subpart F.";
  }
  const gallons = gallonsOf(container?.capacity, container?.capacityUnit);
  const twoSides =
    group === "PORTABLE_TANK" || gallons === undefined || gallons >= DUPLICATE_LABELS_FROM_GALLONS;
  const where = [
    twoSides ? " on two sides" : "",
    group === "PORTABLE_TANK" ? " near the shipping name" : "",
    printsBothLabels ? ", next to each other within 150 mm" : "",
  ].join("");
  return `Apply the labels${where}, or placard per 49 CFR 172 subpart F. ${PRINT_AT_FULL_SCALE}`;
}

export interface BulkMarkingOptions {
  /** The ID-number page prints. Defaults to true. */
  hasIdNumber?: boolean;
  /** The regulated hazard-label pages print for this container. */
  printsLabels?: boolean;
  /** Both a primary and a subsidiary label print. */
  printsBothLabels?: boolean;
  hasInhalationMarking?: boolean;
}

/**
 * What the handler has to do with the markings of a bulk packaging or portable
 * tank, by group.
 *
 * On the marking itself rather than in a help article: the marking sheet is
 * what physically travels to the container, and the person holding it is the
 * person who decides where it goes. Shared rather than written at the render
 * site because Oak's preview shows the same sentence in a notice (the
 * preview renders one page and cannot show the marking page inline).
 */
export function bulkMarkingInstruction(
  group: DotMarkingGroup,
  container?: DotMarkingContainer,
  options: BulkMarkingOptions = {}
): string {
  const {
    hasIdNumber = true,
    printsLabels = false,
    printsBothLabels = false,
    hasInhalationMarking = false,
  } = options;
  const parts = [
    hasIdNumber ? idNumberInstruction(container) : undefined,
    requiresShippingNameOnOpposingSides(group)
      ? portableTankInstruction(group, container)
      : undefined,
    labelOrPlacardInstruction(group, printsLabels, container, printsBothLabels),
    hasInhalationMarking
      ? inhalationMarkingInstruction(inhalationMarkingHeightMm(group, container))
      : undefined,
  ];
  return parts.filter(Boolean).join(" ");
}

/** A package whose class prints no full-size label still prints its ID
 * number marking on a page of its own. */
export const ID_MARKING_PAGE_INSTRUCTION =
  "Apply the shipping name and ID number page to the package. Print it at 100%.";

/**
 * What the handler has to do with the printed hazard label page(s) of a
 * PACKAGE container -- 49 CFR 172.406(a)(1)(ii) and (c) -- and its
 * orientation arrows (172.312(a)). Shared with `bulkMarkingInstruction` above:
 * the PDF and Oak's preview both read this rather than writing their own
 * copy, so the two surfaces cannot say something different.
 */
export function packageLabelInstruction(printsBothLabels: boolean): string {
  const apply = printsBothLabels
    ? "Apply both full-size label pages on the same surface, near the proper shipping name " +
      "marking, within 150 mm of each other."
    : "Apply the full-size label page to the package, on the same surface and near the " +
      "proper shipping name marking.";
  return `${apply} ${PRINT_AT_FULL_SCALE}`;
}

/** 49 CFR 172.312(a)(2): the arrows go on two opposite vertical sides, pointing up. */
export const ORIENTATION_ARROWS_INSTRUCTION =
  "Mark orientation arrows, pointing up, on two opposite vertical sides of the package.";

/** 49 CFR 172.332(b)(2) and 172.334(f), for the orange ID-number panel. */
export const ORANGE_PANEL_INSTRUCTION =
  "Print the panel on a durable material, as 172.519 prescribes for placards " +
  "(49 CFR 172.332(b)(2)). If a placard is required, display the panel in proximity to it " +
  "(172.334(f)).";

/**
 * The Division 6.1 note for a container that may be poisonous by inhalation
 * (see `inhalationMarkingMayBeOwed`). A package swaps the printed POISON
 * label. A bulk packaging or portable tank may carry a label or a placard, and
 * 172.313(a) asks for the INHALATION HAZARD marking on two opposing sides
 * unless those words are already on the label or placard.
 */
export function poisonInhalationNote(
  group: DotMarkingGroup | undefined,
  container?: DotMarkingContainer
): string {
  const zone =
    "If this Division 6.1 material is poisonous by inhalation (a liquid in hazard zone A or B), ";
  const unrecorded =
    "The waste profile does not record the hazard zone, so the label cannot choose. ";
  if (group === undefined || group === "PACKAGE") {
    return (
      `${zone}it takes the POISON INHALATION HAZARD label (49 CFR 172.429) in place of the ` +
      `POISON label printed here. ${unrecorded}Apply that label by hand instead of the printed one.`
    );
  }
  return (
    `${zone}it takes the POISON INHALATION HAZARD label (49 CFR 172.429) or placard ` +
    `(172.555) in place of the POISON one. ${unrecorded}` +
    `When those words are not on the label or placard, also mark ${INHALATION_HAZARD_MARKING} ` +
    `on two opposing sides, at least ${inhalationMarkingHeightMm(group, container)} mm tall ` +
    "(172.313(a), 172.302(b))."
  );
}
